import { useCallback, useEffect, useState } from "react";
import { getApiErrorMessage } from "../../aiClient";
import { ListingEntityType } from "../../components/types/ai.types";
import { socialApi } from "../api/socialApi";
import {
  SocialConnection,
  SocialEntitySyncInput,
  SocialPlatform,
  SocialSyncJob,
  SocialSyncPolicy,
} from "../types/social.types";

const defaultPolicy: SocialSyncPolicy = {
  automationLevel: "manual",

  autoImport: false,
  autoEnhanceImages: true,
  autoGenerateDescriptions: true,
  autoPublishToPickDengu: false,

  publishToFacebook: false,
  publishToInstagram: false,
  publishToTikTok: false,
  publishToGoogleMerchant: false,
  publishToWhatsApp: false,

  requireApprovalBeforeSocialPublish: true,
  requireApprovalBeforePickDenguPublish: true,

  maxAutoPostsPerDay: 3,
};

export const useSocialConnections = () => {
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const rows = await socialApi.getConnections();
      setConnections(rows || []);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, "Failed to load social connections"));
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConnection = useCallback(
    async (connectionId: string, settings: Partial<SocialConnection>) => {
      try {
        setLoading(true);
        setError("");

        const updated = await socialApi.updateConnectionSettings(
          connectionId,
          settings,
        );

        setConnections((prev) =>
          prev.map((connection) =>
            (connection._id || connection.id) === connectionId
              ? updated
              : connection,
          ),
        );

        return updated;
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, "Failed to update connection"));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  return {
    connections,
    loading,
    error,

    loadConnections,
    reload: loadConnections,

    updateConnection,
    setConnections,
  };
};

export const useSocialSync = useSocialConnections;

export const useSocialPolicy = () => {
  const [policy, setPolicy] = useState<SocialSyncPolicy>(defaultPolicy);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const nextPolicy = await socialApi.getPolicy();
      setPolicy(nextPolicy || defaultPolicy);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, "Failed to load social policy"));
      setPolicy(defaultPolicy);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLocalPolicy = useCallback(
    <K extends keyof SocialSyncPolicy>(key: K, value: SocialSyncPolicy[K]) => {
      setPolicy((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const savePolicy = useCallback(
    async (nextPolicy?: SocialSyncPolicy) => {
      const payload = nextPolicy || policy;

      try {
        setSaving(true);
        setError("");

        const saved = await socialApi.savePolicy(payload);
        setPolicy(saved || payload);

        return saved || payload;
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, "Failed to save social policy"));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [policy],
  );

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  return {
    policy,
    setPolicy,
    updateLocalPolicy,
    loading,
    saving,
    error,

    loadPolicy,
    savePolicy,
    save: savePolicy,
  };
};

export const useSocialEntitySync = (
  defaultEntityType?: ListingEntityType,
  defaultEntityId?: string,
) => {
  const [jobs, setJobs] = useState<SocialSyncJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadJobs = useCallback(async () => {
    if (!defaultEntityType || !defaultEntityId) return;

    try {
      setLoading(true);
      setError("");

      const rows = await socialApi.getJobsForEntity(
        defaultEntityType,
        defaultEntityId,
      );

      setJobs(rows || []);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, "Failed to load social jobs"));
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [defaultEntityType, defaultEntityId]);

  const syncEntity = useCallback(
    async (payload: SocialEntitySyncInput) => {
      const finalPayload = {
        ...payload,
        entityType: payload.entityType || defaultEntityType,
        entityId: payload.entityId || defaultEntityId,
      } as SocialEntitySyncInput;

      if (!finalPayload.entityType || !finalPayload.entityId) {
        throw new Error("entityType and entityId are required");
      }

      if (!finalPayload.listing) {
        throw new Error("listing payload is required");
      }

      try {
        setLoading(true);
        setError("");

        const rows = await socialApi.syncEntity(finalPayload);
        setJobs(rows || []);

        return rows || [];
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, "Failed to sync entity"));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [defaultEntityType, defaultEntityId],
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  return {
    jobs,
    loading,
    error,

    loadJobs,
    syncEntity,
    sync: syncEntity,

    setJobs,
  };
};
